import React, { useState, useRef, useEffect } from 'react';
import { theme } from '../theme';
import { CategoryBadge, escapeHtml } from './shared';
import { SECONDARY_AXIS_MAX_VISIBLE } from '../constants';
import { getChatbotBaseUrl, getChatbotHeaders, createConversation as apiCreateConversation } from '../services/chatbotApi';
import BrandPreviewCard from './BrandPreviewCard';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { SecondaryAxisPills } from './SecondaryAxisPills';

// <purpose>Internal chatbot test page — shows full metadata alongside responses for MSB compliance verification</purpose>

// Why: XSS-safe linkify — escape HTML first, then convert known-safe URL patterns to anchors.
// Builds an href="…" attribute below, so it depends on escapeHtml covering the full attribute-
// breakout set (& < > " '), not just the text-context minimum — which it now does (shared.tsx).
// Its second URL regex (below) does NOT exclude " from the match, unlike shared.tsx's
// linkifyUrls — so unlike that sibling, this function has NO independent defence of its own;
// escapeHtml's widened set is the SOLE thing standing between a raw " in an assistant reply and
// an injected attribute here. That is exactly why it must be reachable from a test (task-21
// review finding), not just exercised indirectly through the rendered page.
// Why exported (again): an earlier commit (928e7ff) dropped this export because nothing imported
// it at the time. It now has a genuine consumer — the escapeHtml contract test — so re-exporting
// is a correction, not a reversal of that call.
export const linkifyText = (text: string): string => {
  let s = escapeHtml(text);
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#000d66;word-break:break-all">$1</a>');
  // Why [^\s<&]+ excludes &: this regex already truncated any bare URL containing a raw & (e.g.
  // a query-string join) before task-21 — pre-existing, unrelated to this change. task-21 (the
  // full escapeHtml widening) extends that same truncation to apostrophes, since ' now escapes
  // to &#39; upstream: a URL like https://ex.com/it's-here now truncates at "it" (the &#39; that
  // follows stops the match), leaving "'s-here" to render as plain text after the link instead
  // of inside it. Accepted, not fixed: safe direction (URLs with literal, unencoded apostrophes
  // are non-standard already), cosmetic only, and this is an internal MSB test page — widening
  // the class to tolerate escaped entities would add regex complexity for a rare edge case.
  s = s.replace(/(?<!href=")(https?:\/\/[^\s<&]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#000d66;word-break:break-all">$1</a>');
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  return s;
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
  category?: string;
  reasonCodes?: string[];
  confidence?: number;
  timing?: number;
}

const MOBILE_BREAKPOINT = 768;

const ChatbotTestSeite: React.FC = () => {
  // The chatbot's answer metadata names the same two axes the ticket surfaces do, so it reads them
  // from the same seam instead of restating K-vocabulary.
  const cm = useCategoryMeta();
  const [compact, setCompact] = useState(typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    const onResize = () => setCompact(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [lastMeta, setLastMeta] = useState<{
    category: string; reasonCodes: string[]; confidence: number; timing: number;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Why: only auto-scroll when user is already at the bottom (within 80px threshold).
  // Prevents jarring scroll during streaming when user is reading earlier messages.
  const shouldAutoScroll = useRef(true);

  useEffect(() => {
    if (shouldAutoScroll.current && messagesContainerRef.current) {
      // Why: scrollIntoView scrolls the entire page. Instead, scroll only the messages container.
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleContainerScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScroll.current = distanceFromBottom < 80;
  };

  const createConversation = async (): Promise<string> => {
    if (conversationId) return conversationId;
    const id = await apiCreateConversation();
    setConversationId(id);
    return id;
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    shouldAutoScroll.current = true;

    const start = Date.now();
    try {
      const convId = await createConversation();

      const res = await fetch(
        `${getChatbotBaseUrl()}/chat/conversations/${convId}/messages/stream`,
        {
          method: 'POST',
          headers: await getChatbotHeaders(),
          body: JSON.stringify({ message: text })
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Fehler' }));
        setMessages(prev => [...prev, { role: 'assistant', content: err.message || 'Fehler' }]);
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No stream');

      const decoder = new TextDecoder();
      const state = { fullText: '', meta: { category: '', reasonCodes: [] as string[], confidence: 0 }, assistantIdx: -1 };
      let buffer = '';

      const handleSSELine = (line: string) => {
        if (!line.startsWith('data: ')) return;
        const data = line.slice(6);
        if (data === '[DONE]') return;

        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'classification') {
            state.meta = {
              category: parsed.category,
              reasonCodes: parsed.reasonCodes || [],
              confidence: parsed.confidence || 0
            };
            setMessages(prev => {
              state.assistantIdx = prev.length;
              return [...prev, { role: 'assistant', content: '', ...state.meta }];
            });
          } else if (parsed.type === 'token' && parsed.content) {
            state.fullText += parsed.content;
            const snapshot = state.fullText;
            const idx = state.assistantIdx;
            setMessages(prev => {
              const updated = [...prev];
              const target = idx >= 0 ? idx : updated.length - 1;
              updated[target] = { ...updated[target], content: snapshot };
              return updated;
            });
          }
        } catch { /* skip */ }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        lines.forEach(handleSSELine);
      }

      const timing = Date.now() - start;
      setLastMeta({ ...state.meta, timing });

      // Update final message with timing
      const finalIdx = state.assistantIdx;
      setMessages(prev => {
        const updated = [...prev];
        const target = finalIdx >= 0 ? finalIdx : updated.length - 1;
        updated[target] = { ...updated[target], timing };
        return updated;
      });
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant', content: 'Fehler bei der Verbindung zum Chatbot-Server.'
      }]);
    } finally {
      setSending(false);
    }
  };

  const newConversation = () => {
    setConversationId(null);
    setMessages([]);
    setLastMeta(null);
  };

  const cardStyle: React.CSSProperties = {
    background: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.card,
    overflow: 'hidden',
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: compact ? 'column' : 'row',
      gap: theme.spacing.lg,
      alignItems: compact ? 'stretch' : 'flex-start',
    }}>
      <h1 className="visually-hidden">Chatbot-Test</h1>
      {/* Chat panel */}
      <div style={{
        ...cardStyle, flex: 1, display: 'flex', flexDirection: 'column',
        height: compact ? 'calc(100vh - 200px)' : 'calc(100vh - 160px)',
        minHeight: 400,
      }}>
        {/* Header */}
        <div style={{
          background: theme.colors.navy, color: theme.colors.white,
          padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Chatbot Test</div>
            <div style={{ fontSize: 12, color: theme.colors.textOnDark, fontFamily: 'monospace', marginTop: 2 }}>
              {conversationId ? `Conversation: ${conversationId}` : 'Neue Konversation'}
            </div>
          </div>
          <button onClick={newConversation} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
            padding: '6px 12px', borderRadius: theme.borderRadius.sm, cursor: 'pointer',
            fontSize: 12, fontFamily: theme.font.family,
          }}>
            Neue Konversation
          </button>
        </div>

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          onScroll={handleContainerScroll}
          style={{
          flex: 1, overflowY: 'auto', padding: theme.spacing.lg,
          display: 'flex', flexDirection: 'column', gap: theme.spacing.sm,
        }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: theme.colors.textSecondary, padding: theme.spacing.xl, fontSize: 13 }}>
              Senden Sie eine Nachricht, um den Chatbot zu testen.
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
            }}>
              <div style={{
                padding: '10px 14px',
                borderRadius: 16,
                borderBottomRightRadius: msg.role === 'user' ? 4 : 16,
                borderBottomLeftRadius: msg.role === 'assistant' ? 4 : 16,
                background: msg.role === 'user' ? theme.colors.navy : theme.colors.bgPage,
                color: msg.role === 'user' ? 'white' : theme.colors.navy,
                fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word' as const, wordBreak: 'break-word' as const,
              }}>
                {msg.role === 'assistant' && msg.content
                  ? <span dangerouslySetInnerHTML={{ __html: linkifyText(msg.content) }} />
                  : (msg.content || (sending ? '...' : ''))}
              </div>
              {msg.role === 'assistant' && msg.category && (
                <div style={{
                  fontSize: 11, color: theme.colors.textSecondary, marginTop: 4,
                  display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
                }}>
                  <CategoryBadge category={msg.category} />
                  {/* Plain display, no click/active state, so this adopts SecondaryAxisPills rather
                      than reusing accessors under bespoke markup. This chip used to show the full
                      definition label inline, parenthetical stripped, un-abbreviated and always
                      visible — a DELIBERATE per-surface choice distinct from the Metadaten badge
                      below (see its own history), not an accident. The fleet-wide secondary-axis
                      ruling (SecondaryAxisPills' own header) overrides that intentionally: the face
                      here is de-slugged like every other pill, and the full wording moved to hover
                      instead of staying always-visible. */}
                  <SecondaryAxisPills codes={msg.reasonCodes ?? []} maxVisible={SECONDARY_AXIS_MAX_VISIBLE.chatbot} variant="row" />
                  {msg.timing && (
                    <span style={{ color: theme.colors.navyMuted }}>
                      {(msg.timing / 1000).toFixed(1)}s
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{
          padding: theme.spacing.md, borderTop: `1px solid ${theme.colors.borderLight}`,
          display: 'flex', gap: theme.spacing.sm,
        }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => {
              setInput(e.target.value);
              // Why: auto-grow textarea to fit content, no scrollbar needed
              const el = e.target;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 200) + 'px';
            }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Nachricht eingeben..."
            rows={1}
            style={{
              flex: 1, padding: '10px 14px', border: `1px solid ${theme.colors.borderLight}`,
              borderRadius: 20, outline: 'none', fontFamily: theme.font.family,
              fontSize: 14, resize: 'none', overflow: 'hidden',
              transition: 'height 0.1s ease',
            }}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            style={{
              width: 38, height: 38, borderRadius: '50%', border: 'none',
              background: theme.colors.navy, color: 'white', cursor: 'pointer',
              opacity: (!input.trim() || sending) ? 0.5 : 1, alignSelf: 'flex-end',
              fontFamily: theme.font.family,
            }}
          >
            &#x27A4;
          </button>
        </div>
      </div>

      {/* Right column: brand preview + metadata */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: theme.spacing.lg,
        width: compact ? '100%' : 300, flexShrink: 0,
      }}>
        <BrandPreviewCard />

        {/* Metadata panel */}
        <div style={{ ...cardStyle, padding: theme.spacing.lg }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: theme.colors.navy, marginBottom: theme.spacing.md }}>
            Metadaten
          </h2>

          <MetaRow label="Nachrichten" value={String(messages.length)} />

          {lastMeta && (
            <>
              <div style={{ borderTop: `1px solid ${theme.colors.borderLight}`, margin: `${theme.spacing.md}px 0` }} />
              <h3 style={{
                margin: '12px 0 8px', fontSize: 10, fontWeight: 600,
                letterSpacing: '0.05em', textTransform: 'uppercase', color: theme.colors.navy,
              }}>
                Letzte Antwort
              </h3>
              <MetaRow label={cm.categoryNoun} value={lastMeta.category} highlight />
              <MetaRow label="Konfidenz" value={`${(lastMeta.confidence * 100).toFixed(0)}%`} />
              <MetaRow label="Laufzeit" value={`${(lastMeta.timing / 1000).toFixed(1)}s`} />
              <div style={{ marginTop: theme.spacing.sm }}>
                <div style={{
                  fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
                  color: theme.colors.navyMuted, fontWeight: 600, margin: '12px 0 4px',
                }}>{cm.secondaryAxisNounPlural}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {/* Plain display, no click/active state — SecondaryAxisPills owns this face
                      fleet-wide, same as the per-message chip row above. */}
                  <SecondaryAxisPills codes={lastMeta.reasonCodes ?? []} maxVisible={SECONDARY_AXIS_MAX_VISIBLE.chatbot} variant="row" />
                </div>
              </div>
            </>
          )}

          {!lastMeta && (
            <div style={{ fontSize: 12, color: theme.colors.textSecondary, fontStyle: 'italic' }}>
              Senden Sie eine Nachricht, um Metadaten zu sehen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MetaRow: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 4 }}>
    <span style={{
      fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase',
      color: theme.colors.navyMuted, fontWeight: 600, flexShrink: 0,
    }}>{label}</span>
    <span style={{
      fontSize: 13, fontWeight: highlight ? 700 : 500,
      color: theme.colors.navy,
    }}>{value}</span>
  </div>
);

export default ChatbotTestSeite;
